import {
  knmFilterFormBooleanSelectOptions,
  knmFilterFormSignedSelectOptions,
} from 'KnmListPackage/KnmListPanelModule/constants/knmListFilterOptions';
import { IDictionaryKnoOrganizationSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryKnoOrganizationSelectOption';
import { IDictionarySupervisionTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { IDecisionListFilterRaw } from 'DecisionListPackage/MainModule/interfaces/IDecisionListFilterRaw';

import { IDecisionListFilter } from '../interfaces/IDecisionListFilter';
import { erknmPublishedStatusesSelectOptions } from '../../../CorePackage/MainModule/constants/erknmPublishedStatuses';
import { IKnmFilterForm } from '../../../KnmListPackage/KnmListPanelModule/interfaces/IKnmFilterFormSecondary';
import { IDecisionListFilterSelectOptions } from '../interfaces/IDecisionListFilterSelectOptions';

interface IMapRawToDecisionFilter extends IDecisionListFilterSelectOptions {
  raw: IDecisionListFilterRaw;
}

export function mapRawToDecisionFilter(data: IMapRawToDecisionFilter): IDecisionListFilter {
  const {
    raw,
    organizationsSelectOptions,
    supervisionTypesSelectOptions,
    decisionStatusesSelectOptions,
    countriesSelectOptions,
    reasonsSelectOptions,
    targetEntitiesSelectOptions,
    subjectTypesSelectOptions,
    subjectKindsSelectOptions,
    subjectSubKindsSelectOptions,
    riskCategoriesSelectOptions,
    dangerClassesSelectOptions,
    kindsSelectOptions,
    territorialUnitsSelectOptions,
    userProsecutorOrganisationsSelectOptions,
  } = data;
  const statusesFiltered = decisionStatusesSelectOptions.filter(selectOption => {
    return raw?.statusIds?.some(statusId => statusId === selectOption?.id);
  });
  const countriesFiltered = countriesSelectOptions.filter(selectOption => {
    return raw?.subjectCountryIds?.some(countryId => countryId === selectOption?.id);
  });
  const reasonTypesFiltered = reasonsSelectOptions.filter(selectOption => {
    return raw?.reasonIds?.some(reasonId => reasonId === selectOption?.id);
  });
  const targetEntitiesFiltered = targetEntitiesSelectOptions?.filter(selectOption =>
    raw?.subjectTypeCodes?.some(subjectTypeCode => subjectTypeCode === selectOption.code),
  );
  const subjectKindsFiltered = subjectKindsSelectOptions?.filter(selectOption => {
    return raw?.objectKindIds?.some(objectKindId => objectKindId === selectOption?.id);
  });
  const subjectSubKindsFiltered = subjectSubKindsSelectOptions?.filter(selectOption => {
    return raw?.objectSubKindIds?.some(objectSubKindId => objectSubKindId === selectOption?.id);
  });
  const pubStatusesFiltered = erknmPublishedStatusesSelectOptions?.filter(selectOption =>
    raw?.pubStatuses?.some(pubStatus => pubStatus === selectOption.value),
  );
  const objectTypesFiltered = subjectTypesSelectOptions?.filter(selectOption =>
    raw?.objectTypeIds?.some(objectTypeId => objectTypeId === selectOption.id),
  );
  const supervisionType = supervisionTypesSelectOptions.find(
    (supervisionTypeSelectOption: IDictionarySupervisionTypeSelectOption) =>
      supervisionTypeSelectOption?.id === raw?.controlKindCode,
  );

  const prosecutorOrganizationsFiltered = userProsecutorOrganisationsSelectOptions?.filter(selectOption =>
    raw?.prosecutorOrganizationErvkUuids?.some(
      prosecutorOrganizationErvkUuid => String(prosecutorOrganizationErvkUuid) === selectOption.id,
    ),
  );

  return {
    name: raw?.name,
    years:
      raw?.years?.map(year => ({
        id: String(year),
        label: String(year),
        value: String(year),
      })) || [],
    domains: raw?.domainIds?.reduce((domains: IKnmFilterForm['domains'], domainId) => {
      const territorialUnitSelectOption = territorialUnitsSelectOptions.find(item => item.id === domainId);

      if (territorialUnitSelectOption) {
        const res = domains ?? [];

        res.push(territorialUnitSelectOption);

        return res;
      }

      return domains;
    }, undefined),
    includeDomainChild: raw?.includeDomainChild || false,
    kinds:
      kindsSelectOptions &&
      kindsSelectOptions.filter(kindsSelectOption => raw?.kinds?.some(kind => kind === kindsSelectOption.label)),
    controllingOrganizations: organizationsSelectOptions?.filter(
      (userKnoOrganisationSelectOption: IDictionaryKnoOrganizationSelectOption) =>
        raw?.knoIds?.some(
          controllingOrganizationId => Number(controllingOrganizationId) === Number(userKnoOrganisationSelectOption.id),
        ),
    ),
    includeChildKno: raw?.includeChildKno || false,
    includeChildProsecutorOrg: raw?.includeChildProsecutorOrg || false,
    ignoreKno: raw?.ignoreKno || false,

    supervisionType,
    supervisionTypeId: supervisionType?.code,

    prosecutorOrganizations: prosecutorOrganizationsFiltered?.length ? prosecutorOrganizationsFiltered : undefined,
    reasons: reasonTypesFiltered && reasonTypesFiltered.length ? reasonTypesFiltered : undefined,
    subjectTypes: targetEntitiesFiltered && targetEntitiesFiltered.length ? targetEntitiesFiltered : undefined,
    subjectOrgName: raw?.subjectOrgName,
    subjectOgrn: raw?.subjectOgrn,
    subjectInn: raw?.subjectInn,
    subjectKpp: raw?.subjectKpp,
    subjectCountries: countriesFiltered && countriesFiltered.length ? countriesFiltered : undefined,
    objectAddress: raw?.objectAddress,
    objectTypes: objectTypesFiltered && objectTypesFiltered.length ? objectTypesFiltered : undefined,
    objectKinds: subjectKindsFiltered && subjectKindsFiltered.length ? subjectKindsFiltered : undefined,
    objectSubKinds: subjectSubKindsFiltered && subjectSubKindsFiltered.length ? subjectSubKindsFiltered : undefined,
    objectRiskCategory:
      riskCategoriesSelectOptions &&
      riskCategoriesSelectOptions.find(selectOption => selectOption.id === raw?.objectRiskCategoryId),
    objectDangerClass:
      dangerClassesSelectOptions &&
      dangerClassesSelectOptions.find(selectOption => selectOption.id === raw?.objectDangerClassId),
    signed: knmFilterFormSignedSelectOptions.find(selectOption => selectOption.value === raw?.signed),
    statuses: statusesFiltered.length ? statusesFiltered : undefined,
    pubStatuses: pubStatusesFiltered.length ? pubStatusesFiltered : undefined,
    number: raw?.number,
    deletedInclude: knmFilterFormBooleanSelectOptions.find(selectOption => selectOption.value === raw?.deletedInclude),
    deletedOnly: knmFilterFormBooleanSelectOptions.find(selectOption => selectOption.value === raw?.deletedOnly),
  };
}
